import type {
  DataGovernanceOverview,
  DataPlatformGovernance,
} from "@/api/temp/TrustAIExecutiveService";

/**
 * The Data Estate side of the Governance Landscape.
 *
 * Only the per-platform rows below are authored. The three-part overview
 * (Data Sources / Monitoring Policies / Access Controls) is summed from them by
 * `buildDataGovernanceOverview`, so the summary and the platform cards can
 * never disagree - the most common failure mode for a screen that shows the
 * same number at two levels.
 *
 * Vocabulary note: the three dimensions are independent, not a single rolled-up
 * score. An access control can fail on a data source whose governance setup is
 * complete, which is why a platform can carry failed controls and still report
 * every data source as Secured. Collapsing them into one "protected" number is
 * exactly what this section is designed not to do.
 */

/** Four platforms carry every incomplete data source; the rest are complete. */
const ATTENTION_PLATFORMS: DataPlatformGovernance[] = [
  {
    id: "snowflake",
    name: "Snowflake",
    serviceType: "snowflake",
    dataSourceTotal: 4,
    securedCount: 2,
    incompleteCount: 2,
    monitoringPolicies: { total: 142, enabled: 118, disabled: 24 },
    accessControls: { total: 96, deployed: 82, saved: 9, failed: 5 },
    attentionItems: [
      {
        id: "snowflake-customer-analytics",
        name: "Customer Analytics",
        status: "INCOMPLETE",
        details: [
          "5 failed access controls",
          "24 disabled monitoring policies",
        ],
      },
      {
        id: "snowflake-sandbox",
        name: "Sandbox",
        status: "NOT_EVALUATED",
        details: ["Monitoring and access-control evaluation is incomplete"],
      },
    ],
    accessingAgentCount: 27,
    providingMcpServerCount: 4,
  },
  {
    id: "databricks",
    name: "Databricks",
    serviceType: "databricks",
    dataSourceTotal: 2,
    securedCount: 1,
    incompleteCount: 1,
    monitoringPolicies: { total: 118, enabled: 92, disabled: 26 },
    accessControls: { total: 92, deployed: 44, saved: 40, failed: 4 },
    attentionItems: [
      {
        id: "databricks-analytics-workspace",
        name: "Analytics Workspace",
        status: "INCOMPLETE",
        details: ["Agent tool access is not covered by the monitoring policy"],
      },
    ],
    accessingAgentCount: 18,
    providingMcpServerCount: 3,
  },
  {
    id: "oracle",
    name: "Oracle",
    serviceType: "oracle",
    dataSourceTotal: 7,
    securedCount: 6,
    incompleteCount: 1,
    monitoringPolicies: { total: 131, enabled: 101, disabled: 30 },
    accessControls: { total: 78, deployed: 38, saved: 33, failed: 3 },
    attentionItems: [
      {
        id: "oracle-finance-reporting",
        name: "Finance Reporting",
        status: "INCOMPLETE",
        details: ["A regional sensitive-data policy is missing"],
      },
    ],
    accessingAgentCount: 9,
    providingMcpServerCount: 2,
  },
  {
    id: "sqlserver",
    name: "SQL Server",
    serviceType: "sqlserver",
    dataSourceTotal: 12,
    securedCount: 10,
    incompleteCount: 2,
    monitoringPolicies: { total: 168, enabled: 122, disabled: 46 },
    accessControls: { total: 118, deployed: 50, saved: 56, failed: 6 },
    attentionItems: [
      {
        id: "sqlserver-benchmark",
        name: "Benchmark Data Source",
        status: "INCOMPLETE",
        details: ["Access-control deployment has repeatedly failed"],
      },
      {
        id: "sqlserver-eu-operations",
        name: "EU Operations",
        status: "NOT_EVALUATED",
        details: ["Monitoring-policy evaluation is pending"],
      },
    ],
    accessingAgentCount: 12,
    providingMcpServerCount: 3,
  },
];

/**
 * The remaining eleven platforms, every data source complete.
 *
 * They exist so the estate's fifteen discovered data platforms all appear under
 * "Show: All", and so the overview totals are a real sum rather than four rows
 * plus a fudge factor. Under the default "Needs Attention" filter none of them
 * render.
 */
const COMPLETE_PLATFORMS: DataPlatformGovernance[] = (
  [
    ["sharepoint", "SharePoint", "sharepoint", 2, [36, 26, 10], [35, 11, 14, 7], 14, 3],
    ["fabric", "Microsoft Fabric", "powerbi", 1, [43, 31, 12], [41, 13, 17, 8], 11, 2],
    ["s3", "Amazon S3", "s3", 1, [31, 22, 9], [29, 9, 12, 6], 8, 2],
    ["bigquery", "Google BigQuery", "bigquery", 1, [33, 24, 9], [32, 10, 13, 6], 7, 2],
    ["workday", "Workday", "workday", 1, [26, 19, 7], [25, 8, 11, 4], 6, 1],
    ["postgresql", "PostgreSQL", "postgresql", 1, [23, 17, 6], [21, 7, 8, 4], 5, 1],
    ["salesforce", "Salesforce", "salesforce", 1, [29, 21, 8], [26, 9, 11, 5], 9, 2],
    ["servicenow", "ServiceNow", "servicenow", 1, [19, 14, 5], [17, 6, 7, 3], 4, 1],
    ["adls", "Azure Data Lake", "azure", 1, [25, 18, 7], [22, 7, 10, 4], 6, 1],
    ["mongodb", "MongoDB", "mongodb", 1, [22, 16, 6], [19, 6, 8, 4], 4, 1],
    ["redshift", "Redshift", "redshift", 1, [22, 16, 6], [19, 6, 8, 4], 5, 1],
  ] as const
).map(
  ([
    id,
    name,
    serviceType,
    dataSourceTotal,
    monitoring,
    access,
    accessingAgentCount,
    providingMcpServerCount,
  ]) => ({
    id,
    name,
    serviceType,
    dataSourceTotal,
    securedCount: dataSourceTotal,
    incompleteCount: 0,
    monitoringPolicies: {
      total: monitoring[0],
      enabled: monitoring[1],
      disabled: monitoring[2],
    },
    accessControls: {
      total: access[0],
      deployed: access[1],
      saved: access[2],
      failed: access[3],
    },
    attentionItems: [],
    accessingAgentCount,
    providingMcpServerCount,
  }),
);

export const dataPlatformGovernance: DataPlatformGovernance[] = [
  ...ATTENTION_PLATFORMS,
  ...COMPLETE_PLATFORMS,
];

const sum = (
  pick: (platform: DataPlatformGovernance) => number | null,
): number =>
  dataPlatformGovernance.reduce(
    (total, platform) => total + (pick(platform) ?? 0),
    0,
  );

/**
 * Summed, never authored.
 *
 * `total` is its own field rather than `deployed + saved + failed`: access
 * controls also sit in states the summary does not break out, so the three
 * named states legitimately do not account for the whole.
 */
export const buildDataGovernanceOverview = (): DataGovernanceOverview => ({
  dimensions: [
    {
      id: "data-sources",
      total: sum(platform => platform.dataSourceTotal),
      stats: [
        { id: "secured", value: sum(platform => platform.securedCount) },
        {
          id: "incomplete",
          value: sum(platform => platform.incompleteCount),
          attention: true,
        },
      ],
    },
    {
      id: "monitoring-policies",
      total: sum(platform => platform.monitoringPolicies.total),
      stats: [
        {
          id: "enabled",
          value: sum(platform => platform.monitoringPolicies.enabled),
        },
        {
          id: "disabled",
          value: sum(platform => platform.monitoringPolicies.disabled),
          attention: true,
        },
      ],
    },
    {
      id: "access-controls",
      total: sum(platform => platform.accessControls.total),
      stats: [
        {
          id: "deployed",
          value: sum(platform => platform.accessControls.deployed),
        },
        { id: "saved", value: sum(platform => platform.accessControls.saved) },
        {
          id: "failed",
          value: sum(platform => platform.accessControls.failed),
          attention: true,
        },
      ],
    },
  ],
});
